import { Menu, MenuItem, Tooltip } from "@mui/material";
import { trackMenuStyles } from "./styles";
import {
  handleAddTrackVersion,
  addNewVersion,
  downloadSong,
  addDolbyEffect,
} from "../TrackComponents/TrackHandler/trackHandlers";
import { TrackMenuProps } from "./types";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { useContext, useState } from "react";
import { CustomIconButton } from "../../pages/Tracks/styles";
import { THEME } from "../../theme";
import { MasterModal } from "../MasteringComponents/MasterModal";
import {
  AuthContext,
  S3CredentialsContext,
  S3CredentialsContextInterface,
  UserDetailsContext,
} from "../../contexts";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { SplitSheet } from "../Modal/SplitSheet";
import {
  GetUserResponse,
  IncrementEnterpriseMasteringCountResponse,
  SubscriptionTier,
} from "../../proto/api_pb";
import { incrementMasteringCount } from "../../services/masteringService";
import { isBillingRequired } from "../../utils/subscriptionBillingUtils";
import { useGetUser } from "../../hooks/useGetUser";
import { getUser } from "../../services/userService";
import { handleUpdateFreeTierMetadata } from "./helper";

export const TrackMenu: React.FC<TrackMenuProps> = ({
  track,
  handleOpenCollabsModal,
  handleOpenEditTrackModal,
  handleOpenSelectDeleteModal,
  handleRefreshTracks,
  handleOpenUpdateSubModal,
  allowDownload,
}) => {
  const [isMasterModalOpen, setIsMasterModalOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [state, setState] = useState("Uploading track...");
  const [selectedPreset, setSelectedPreset] = useState("Bubbly");
  const [selectedPresetVal, setSelectedPresetVal] = useState("a");
  const [sliderValue, setSliderValue] = useState(-14);
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const { userId } = useContext(AuthContext);
  const [masteredMusicUrl, setMasteredMusicUrl] = useState<string | undefined>(
    undefined
  );
  const [previewUrl, setPreviewUrl] = useState<Map<string, string> | undefined>(
    undefined
  );
  const [previewState, setPreviewState] = useState("Uploading track...");
  const [trackMenuAnchorEl, setTrackMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const { isEnterpriseAccount, enterprise, subscriptionTier, storageUsage } =
    useContext(UserDetailsContext);
  const openTrackMenu = Boolean(trackMenuAnchorEl);
  const { getValidAccessToken } = useGetValidAccessToken();
  const { getUserResponse } = useGetUser(userId);
  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);
  const isSharedContentPage = window.location.pathname.includes("/share/");

  const handleTrackMenuClose = () => setTrackMenuAnchorEl(null);
  const handleOpenTrackMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setTrackMenuAnchorEl(event.currentTarget);
  };

  const getDolbyEffect = async (
    selectedPresetArray: string[],
    sliderValue: number,
    trackUrl: string,
    trackVersionId: string,
    previewStartValue: number | undefined,
    duration: number | undefined
  ) => {
    const accessToken = await getValidAccessToken();
    addDolbyEffect(
      selectedPresetArray,
      sliderValue,
      accessToken,
      trackUrl,
      trackVersionId,
      setPreviewUrl,
      setPreviewLoading,
      handlePreviewState,
      previewStartValue,
      duration
    );
  };

  const handleState = (state: string) => setState(state);
  const handlePreviewState = (state: string) => setPreviewState(state);

  const processTrackVersion = async (
    sliderValue: number,
    selectedPresetValue: string,
    trackUrl: string,
    trackVersionId: string,
    isFree?: boolean
  ) => {
    try {
      const token = await getValidAccessToken();
      handleAddTrackVersion(
        selectedPresetValue,
        sliderValue,
        token,
        trackUrl,
        trackVersionId,
        setMasteredMusicUrl,
        setPreviewLoading,
        handleState,
        setJobId,
        isFree
      );
      setSliderValue(sliderValue);
      setSelectedPresetVal(selectedPresetValue);
    } catch (error) {
      console.error(error);
    }
  };

  const handleMasteringOnMenuClicked = () => {
    const freeTierMetadata = getUserResponse?.getFreeTierMetadata?.();
    const hasEligibleFreeMastering =
      (freeTierMetadata?.getTracksUploadedForFreeMastering?.() ?? 0) >= 20 &&
      (freeTierMetadata?.getMasteringCount?.() ?? 0) < 1 &&
      subscriptionTier === SubscriptionTier.SUBSCRIPTION_TIER_FREE;

    if (
      isBillingRequired(subscriptionTier, storageUsage) &&
      !isEnterpriseAccount &&
      !hasEligibleFreeMastering
    ) {
      handleOpenUpdateSubModal();
    } else {
      setIsMasterModalOpen(true);
    }
  };

  const createNewVersion = async () => {
    try {
      const token = await getValidAccessToken();
      if (track.track) {
        await addNewVersion(
          track.track,
          masteredMusicUrl,
          selectedPreset,
          jobId,
          previewUrl?.get(selectedPresetVal),
          token,
          track.track?.getId(),
          userId,
          handleRefreshTracks,
          sliderValue,
          isEnterpriseAccount ? enterprise?.id : undefined
        );
        if (enterprise?.id) {
          const res = await incrementMasteringCount(
            token,
            enterprise.id,
            userId
          );
          if (res instanceof IncrementEnterpriseMasteringCountResponse) {
            console.log("Mastering Count Incremented");
          }
        }
        if (
          !isEnterpriseAccount &&
          subscriptionTier === SubscriptionTier.SUBSCRIPTION_TIER_FREE
        ) {
          const user = await getUser(userId, token);
          if (!(user instanceof GetUserResponse)) return;
          await handleUpdateFreeTierMetadata({
            userId,
            token,
            awsAccessKeyId,
            awsSecretAccessKey,
            awsSessionToken,
            masteringCount:
              (user.getFreeTierMetadata()?.getMasteringCount() ?? 0) + 1,
            tracksUploaded:
              user.getFreeTierMetadata()?.getTracksUploadedForFreeMastering() ??
              0,
            user,
          });
        }
      }
    } catch (error) {
      console.error("New Version After Mastering Error:", error);
    } finally {
      setIsMasterModalOpen(false);
    }
  };

  const shouldShowDownload = !isSharedContentPage || allowDownload;
  const shouldShowMenu = !isSharedContentPage || allowDownload;

  if (!shouldShowMenu) return null;

  return (
    <>
      <Tooltip title="More" placement="top">
        <CustomIconButton
          aria-controls={openTrackMenu ? "basic-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={openTrackMenu ? "true" : undefined}
          onClick={handleOpenTrackMenu}
        >
          <img
            style={{
              filter: THEME === "light" ? "invert(100%)" : "contrast(300%)",
              width: "17px",
              height: "17px",
            }}
            src="/assets/icons/TracksPageIcons/dots-vertical.svg"
            alt="icon"
          />
        </CustomIconButton>
      </Tooltip>
      <Menu
        id="basic-menu"
        anchorEl={trackMenuAnchorEl}
        open={openTrackMenu}
        onClose={handleTrackMenuClose}
        PaperProps={{ style: trackMenuStyles.paperProps }}
        sx={trackMenuStyles.menu}
        onClick={(e) => e.stopPropagation()}
      >
        {[
          shouldShowDownload && (
            <MenuItem
              key="download"
              onClick={async () => {
                await downloadSong(
                  track.trackUrl,
                  track.track?.getTitle(),
                  "",
                  "",
                  ""
                );
                handleTrackMenuClose();
              }}
            >
              <img
                style={{
                  margin: "2px",
                  marginRight: "8px",
                  filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
                }}
                src="/assets/icons/TracksPageIcons/download-01.svg"
                alt=""
              />
              Download
            </MenuItem>
          ),

          !isSharedContentPage && (
            <MenuItem
              key="edit"
              onClick={() => {
                handleTrackMenuClose();
                handleOpenEditTrackModal && handleOpenEditTrackModal(track);
              }}
            >
              <img
                style={{
                  margin: "2px",
                  marginRight: "8px",
                  filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
                }}
                src="/assets/icons/TracksPageIcons/edit-05.svg"
                alt=""
              />
              Edit
            </MenuItem>
          ),

          !isSharedContentPage && (
            <MenuItem
              key="delete"
              onClick={() => {
                handleTrackMenuClose();
                handleOpenSelectDeleteModal &&
                  handleOpenSelectDeleteModal(track);
              }}
            >
              <DeleteOutlinedIcon
                style={{
                  margin: "2px",
                  marginRight: "8px",
                  filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
                }}
              />
              Delete
            </MenuItem>
          ),

          !isSharedContentPage && (
            <MenuItem key="master" onClick={handleMasteringOnMenuClicked}>
              <img
                style={{
                  margin: "2px",
                  marginRight: "8px",
                  filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
                }}
                src="/assets/icons/TracksPageIcons/dolby.svg"
                alt=""
              />
              Dolby Mastering
            </MenuItem>
          ),

          isMasterModalOpen && track.track && (
            <MasterModal
              key="master-modal"
              isMasterModalOpen={isMasterModalOpen}
              handleMasterModalOpen={() => setIsMasterModalOpen(true)}
              handleMasterModalClose={() => setIsMasterModalOpen(false)}
              track={track}
              addDolbyEffect={getDolbyEffect}
              masteredMusicUrl={masteredMusicUrl}
              handleAddTrackVersion={processTrackVersion}
              state={state}
              previewState={previewState}
              previewLoading={previewLoading}
              setPreviewLoading={setPreviewLoading}
              previewUrl={previewUrl}
              addNewVersion={createNewVersion}
              trackUrl={track.trackUrl}
              trackVersionId={track.track?.getTrackVersionId()}
              setSelectedPreset={setSelectedPreset}
            />
          ),

          !isSharedContentPage && (
            <MenuItem
              key="collabs"
              onClick={() => {
                handleTrackMenuClose();
                handleOpenCollabsModal && handleOpenCollabsModal(track);
              }}
            >
              <img
                style={{
                  margin: "2px",
                  marginRight: "8px",
                  filter: THEME === "light" ? "invert(100%)" : "invert(0%)",
                }}
                src="/assets/icons/TracksPageIcons/users-plus.svg"
                alt=""
              />
              Edit Collaborators
            </MenuItem>
          ),

          isEnterpriseAccount && (
            <SplitSheet
              key="splitsheet-1"
              handleTrackMenuClose={handleTrackMenuClose}
              track={track}
              isPublishingSplitSheetDetails={false}
            />
          ),

          isEnterpriseAccount && (
            <SplitSheet
              key="splitsheet-2"
              handleTrackMenuClose={handleTrackMenuClose}
              track={track}
              isPublishingSplitSheetDetails={true}
            />
          ),
        ].filter(Boolean)}
      </Menu>
    </>
  );
};
